import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Alert, CircularProgress, Divider, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Button, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
  Checkbox, TextField, InputAdornment, useMediaQuery, useTheme,
} from '@mui/material';
import { MenuBook, People, EditNote, FactCheck, Assessment, School, CalendarMonth, ArrowForward, Search, Groups } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function StaffTeaching() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [scope,   setScope]   = useState(null);   // { classTeacherOf, subjectClasses }
  const [loading, setLoading] = useState(true);
  const [notice,  setNotice]  = useState('');

  const [roster,      setRoster]      = useState(null);  // { classCode, className, areaCode, areaName, isMandatory, learners }
  const [rosterLoad,  setRosterLoad]  = useState(false);
  const [savingAdm,   setSavingAdm]   = useState(null);  // admNo currently being toggled

  // Combined roster of every learner across the teacher's subjects/classes.
  const [myLearners,     setMyLearners]     = useState(null);   // { academicYear, term, learners:[] }
  const [myLearnersLoad, setMyLearnersLoad] = useState(true);
  const [learnerQuery,   setLearnerQuery]   = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/academics/class-teacher/teaching-scope`, { headers: hdrs() });
        setScope(data);
      } catch (err) {
        // 403 = account isn't a teacher; anything else is transient
        setNotice(err.response?.status === 403
          ? 'Your account is not set up as a teacher. Ask your administrator to assign you subjects.'
          : (err.response?.data?.error || 'Could not load your teaching.'));
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/academics/class-teacher/my-learners`, { headers: hdrs() });
        setMyLearners(data);
      } catch { setMyLearners({ learners: [] }); }
      finally { setMyLearnersLoad(false); }
    })();
  }, []);

  const subjectClasses = scope?.subjectClasses || [];
  const classTeacherOf = scope?.classTeacherOf || [];

  const openStudents = async (sc) => {
    setRoster({ classCode: sc.classCode, className: sc.className, areaCode: sc.areaCode, areaName: sc.areaName, learners: null });
    setRosterLoad(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/class-teacher/subject-roster-manage`, {
        headers: hdrs(), params: { classCode: sc.classCode, areaCode: sc.areaCode },
      });
      setRoster(r => ({ ...r, ...data }));
    } catch (err) {
      setRoster(r => ({ ...r, learners: [], error: err.response?.data?.error || 'Could not load students' }));
    } finally { setRosterLoad(false); }
  };

  // Add/remove a learner on this subject. Only optional subjects are toggleable,
  // and only for learners with an open (Active) term registration.
  const toggleTaking = async (l) => {
    if (!roster || roster.isMandatory || !l.registered || l.regStatus !== 'Active') return;
    setSavingAdm(l.admNo);
    setRoster(r => ({ ...r, error: '' }));
    try {
      await axios.post(`${API}/api/academics/class-teacher/subject-roster`, {
        classCode: roster.classCode, areaCode: roster.areaCode,
        admNo: l.admNo, action: l.taking ? 'remove' : 'add',
      }, { headers: hdrs() });
      setRoster(r => ({
        ...r,
        learners: r.learners.map(x => x.admNo === l.admNo ? { ...x, taking: !x.taking } : x),
      }));
    } catch (err) {
      setRoster(r => ({ ...r, error: err.response?.data?.error || 'Update failed' }));
    } finally { setSavingAdm(null); }
  };

  if (loading) return (
    <Box sx={{ textAlign: 'center', mt: 8 }}><CircularProgress /><Typography mt={2}>Loading…</Typography></Box>
  );

  const Actions = ({ sc }) => (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      <Tooltip title="Students taking this subject — add or remove">
        <Button size="small" startIcon={<People />} onClick={() => openStudents(sc)}>Students</Button>
      </Tooltip>
      <Tooltip title="Enter marks">
        <Button size="small" startIcon={<EditNote />} onClick={() => navigate('/academics/score-entry')}>Marks</Button>
      </Tooltip>
      <Tooltip title="Assessment setup (rubrics)">
        <Button size="small" startIcon={<FactCheck />} onClick={() => navigate('/academics/assessment-setup')}>Setup</Button>
      </Tooltip>
      <Tooltip title="Assessment report">
        <Button size="small" startIcon={<Assessment />} onClick={() => navigate('/academics/assessment-report')}>Report</Button>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: { xs: 1.5, sm: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      {/* My Classes — classes this teacher is class teacher of */}
      {classTeacherOf.length > 0 && (
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
              <School sx={{ color: '#1565c0' }} />
              <Typography variant="h6" fontWeight={700}>My Class{classTeacherOf.length > 1 ? 'es' : ''}</Typography>
              <Chip size="small" label={`${classTeacherOf.length} as class teacher`} variant="outlined" sx={{ ml: 0.5 }} />
              <Button size="small" startIcon={<CalendarMonth />} sx={{ ml: 'auto' }}
                onClick={() => navigate('/staff/timetable')}>My Timetable</Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={1}>
              {classTeacherOf.map(c => (
                <Box key={c.classCode} display="flex" alignItems="center" justifyContent="space-between" gap={1}
                  sx={{ p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{c.className || c.classCode}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.classCode}</Typography>
                  </Box>
                  <Button size="small" variant="outlined" endIcon={<ArrowForward />}
                    onClick={() => navigate('/class-teacher-centre')}>Open class centre</Button>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
            <MenuBook sx={{ color: '#1b5e20' }} />
            <Typography variant="h6" fontWeight={700}>My Subjects</Typography>
            {subjectClasses.length > 0 && (
              <Chip size="small" label={`${subjectClasses.length} subject class${subjectClasses.length > 1 ? 'es' : ''}`} variant="outlined" sx={{ ml: 0.5 }} />
            )}
            {classTeacherOf.length === 0 && (
              <Button size="small" startIcon={<CalendarMonth />} sx={{ ml: 'auto' }}
                onClick={() => navigate('/staff/timetable')}>My Timetable</Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}

          {!notice && subjectClasses.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
              You have no subject classes assigned yet.
            </Typography>
          ) : isMobile ? (
            <Box display="flex" flexDirection="column" gap={1.2}>
              {subjectClasses.map((sc, i) => (
                <Card key={`${sc.classCode}-${sc.areaCode}-${i}`} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" fontWeight={700}>{sc.className || sc.classCode}</Typography>
                    <Chip label={sc.areaName || sc.areaCode} size="small" variant="outlined" sx={{ my: 0.5 }} />
                    <Box mt={0.5}><Actions sc={sc} /></Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Class</strong></TableCell>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subjectClasses.map((sc, i) => (
                    <TableRow key={`${sc.classCode}-${sc.areaCode}-${i}`} hover>
                      <TableCell>{sc.className || sc.classCode}</TableCell>
                      <TableCell><Chip label={sc.areaName || sc.areaCode} size="small" variant="outlined" /></TableCell>
                      <TableCell align="right"><Actions sc={sc} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Combined roster — every learner across the teacher's subjects/classes */}
      <Card elevation={3} sx={{ mt: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
            <Groups sx={{ color: '#00695c' }} />
            <Typography variant="h6" fontWeight={700}>All My Learners</Typography>
            {(myLearners?.learners || []).length > 0 && (
              <Chip size="small" label={`${myLearners.learners.length} learner${myLearners.learners.length > 1 ? 's' : ''}`} variant="outlined" sx={{ ml: 0.5 }} />
            )}
            {myLearners?.academicYear && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {myLearners.academicYear} · Term {myLearners.term}
              </Typography>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {myLearnersLoad ? (
            <Box textAlign="center" py={3}><CircularProgress size={24} /></Box>
          ) : (myLearners?.learners || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
              No learners found across your subjects yet.
            </Typography>
          ) : (() => {
            const q = learnerQuery.trim().toLowerCase();
            const rows = (myLearners.learners || []).filter(l =>
              !q || l.name.toLowerCase().includes(q) || (l.admNo || '').toLowerCase().includes(q) ||
              (l.className || '').toLowerCase().includes(q) ||
              (l.subjects || []).some(s => s.toLowerCase().includes(q)));
            return (
              <>
                <TextField size="small" fullWidth placeholder="Search name, adm no, class or subject…"
                  value={learnerQuery} onChange={e => setLearnerQuery(e.target.value)} sx={{ mb: 2 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell><strong>Adm No</strong></TableCell>
                        <TableCell><strong>Class</strong></TableCell>
                        <TableCell><strong>Subjects</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((l, i) => (
                        <TableRow key={l.admNo} hover>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{l.name}</TableCell>
                          <TableCell><Typography variant="caption" fontFamily="monospace">{l.admNo}</Typography></TableCell>
                          <TableCell>{l.className || l.classCode}</TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {l.classTeacher && <Chip label="Class teacher" size="small" color="info" variant="outlined" />}
                              {(l.subjects || []).map(s => <Chip key={s} label={s} size="small" variant="outlined" />)}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {rows.length === 0 && (
                        <TableRow><TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary" py={2}>No learners match “{learnerQuery}”.</Typography>
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  Showing {rows.length} of {myLearners.learners.length}
                </Typography>
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Students dialog — add / remove learners for this subject */}
      <Dialog open={!!roster} onClose={() => setRoster(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography fontWeight={700}>{roster?.className}</Typography>
          <Typography variant="caption" color="text.secondary">
            {roster?.areaName} · students
            {roster?.academicYear ? ` · ${roster.academicYear} Term ${roster.term}` : ''}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {rosterLoad ? (
            <Box textAlign="center" py={3}><CircularProgress size={24} /></Box>
          ) : roster?.error && !(roster?.learners || []).length ? (
            <Alert severity="error">{roster.error}</Alert>
          ) : (roster?.learners || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2} textAlign="center">No students found.</Typography>
          ) : (
            <>
              {roster?.error && <Alert severity="error" sx={{ mb: 1 }}>{roster.error}</Alert>}
              {roster?.offeredInStage === false ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  This subject isn't set up in the stage's Stage-Subjects for {roster?.academicYear}, so enrolment
                  can't be changed here. Ask an administrator to add it under Academics → Stage Subjects.
                </Alert>
              ) : roster?.isMandatory ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  This is a <strong>mandatory</strong> subject — every learner takes it, so it can't be added or removed.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Tick a learner to add them to this subject, untick to remove. Only learners with an open
                  term registration can be changed.
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {roster.learners.filter(l => l.taking).length} of {roster.learners.length} taking this subject
              </Typography>
              <List dense>
                {roster.learners.map((l, idx) => {
                  const lockable = roster.offeredInStage !== false && !roster.isMandatory && l.registered && l.regStatus === 'Active';
                  const note = !l.registered
                    ? 'Not registered for the term'
                    : l.regStatus !== 'Active'
                      ? `Registration ${l.regStatus?.toLowerCase()}`
                      : '';
                  return (
                    <ListItem key={l.admNo} divider disableGutters
                      secondaryAction={
                        savingAdm === l.admNo
                          ? <CircularProgress size={18} sx={{ mr: 1 }} />
                          : <Checkbox edge="end" checked={!!l.taking}
                              disabled={!lockable}
                              onChange={() => toggleTaking(l)} />
                      }>
                      <ListItemText
                        primary={`${idx + 1}. ${l.name}`}
                        secondary={note ? `${l.admNo} · ${note}` : l.admNo}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoster(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
